import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Button from "../../../../componentes/button/button";
import Input from "../../../../componentes/input/input";
import InputMoney from "../../../../componentes/input/inputMoney";
import { CustomDropdown } from "../../../../componentes/dropdown/CustomDropdown";
import CentroCustoService from "../../../../services/resource/CentroCustoService";
import ContaFinanceiraService from "../../../../services/resource/contaFinanceiraService";
import { formatarMoeda } from "../../../../utils/solicitacoes/formatters";
import { somaBateComTotal } from "../../../../utils/solicitacoes/orcamento";

const centroCustoService = new CentroCustoService();
const contaFinanceiraService = new ContaFinanceiraService();

const rotuloCentroCusto = c => `${c.codigo} · ${c.descricao}`;
const rotuloContaFinanceira = c => `${c.codigo} · ${c.nome}`;

/**
 * Rateio de receita/despesa (Lançamento de Despesa do tipo Contas a Pagar) — sem Conta BP: cada
 * linha é montada à mão com Filial, Centro de Custo, Conta Financeira (cadastro BP > Cadastros > Conta
 * Financeira, só as ativas), Valor e Nota.
 * Além dos códigos enviados ao backend, cada linha guarda os nomes exibidos (filialNome,
 * centroCustoDescricao, contaFinanceiraNome) para a tabela do PDF assinado e o detalhe.
 */
export default function RateioReceitaDespesaEditor({ itens = [], onChange, filiais = [], filialPadraoId, valorTotal }) {
    const [centrosCusto, setCentrosCusto] = useState([]);
    const [contasFinanceiras, setContasFinanceiras] = useState([]);
    const [carregandoContas, setCarregandoContas] = useState(true);

    useEffect(() => {
        centroCustoService.listar().then(res => setCentrosCusto(res.data)).catch(() => setCentrosCusto([]));
        contaFinanceiraService.listarAtivas()
            .then(res => setContasFinanceiras(res.data))
            .catch(() => {
                setContasFinanceiras([]);
                toast.error("Não foi possível carregar as contas financeiras.");
            })
            .finally(() => setCarregandoContas(false));
    }, []);

    function adicionar() {
        const filial = filiais.find(f => f.id === filialPadraoId);
        onChange([...itens, {
            filialId: filial?.id ?? null,
            filialNome: filial?.nome ?? "",
            centroCustoCodigo: null,
            centroCustoDescricao: "",
            contaFinanceiraCodigo: null,
            contaFinanceiraNome: "",
            valor: null,
            numeroNotaFiscal: "",
        }]);
    }

    function atualizar(index, campos) {
        onChange(itens.map((item, i) => i === index ? { ...item, ...campos } : item));
    }

    function selecionarFilial(index, filialId) {
        const filial = filiais.find(f => f.id === filialId);
        atualizar(index, { filialId, filialNome: filial?.nome ?? "" });
    }

    function selecionarCentroCusto(index, codigo) {
        const centro = centrosCusto.find(c => c.codigo === codigo);
        atualizar(index, { centroCustoCodigo: codigo, centroCustoDescricao: centro?.descricao ?? "" });
    }

    function selecionarContaFinanceira(index, codigo) {
        const conta = contasFinanceiras.find(c => c.codigo === codigo);
        atualizar(index, { contaFinanceiraCodigo: codigo, contaFinanceiraNome: conta?.nome ?? "" });
    }

    function remover(index) {
        onChange(itens.filter((_, i) => i !== index));
    }

    const totalRateado = itens.reduce((soma, item) => soma + (Number(item.valor) || 0), 0);
    const totalConfere = somaBateComTotal(totalRateado, valorTotal);

    return (
        <div className="flex flex-col gap-3">
            {itens.length > 0 && (
                <div className="overflow-x-auto border border-border rounded-lg">
                    <table className="w-full text-xs">
                        <thead className="bg-muted">
                            <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                                <th className="px-3 py-2 font-semibold min-w-40">Filial</th>
                                <th className="px-3 py-2 font-semibold min-w-52">Centro de Custo</th>
                                <th className="px-3 py-2 font-semibold min-w-64">Conta Financeira</th>
                                <th className="px-3 py-2 font-semibold min-w-36">Valor</th>
                                <th className="px-3 py-2 font-semibold min-w-28">Nota</th>
                                <th className="px-3 py-2 w-9" />
                            </tr>
                        </thead>
                        <tbody>
                            {itens.map((item, index) => (
                                <tr key={index} className="border-t border-border align-middle">
                                    <td className="px-3 py-2">
                                        <CustomDropdown value={item.filialId} onChange={e => selecionarFilial(index, e.value)}
                                            options={filiais} optionValue="id" optionLabel="nome" placeholder="Filial" filter />
                                    </td>
                                    <td className="px-3 py-2">
                                        <CustomDropdown value={item.centroCustoCodigo} onChange={e => selecionarCentroCusto(index, e.value)}
                                            options={centrosCusto} optionValue="codigo" optionLabel={rotuloCentroCusto}
                                            placeholder="Centro de custo" filter />
                                    </td>
                                    <td className="px-3 py-2">
                                        <CustomDropdown value={item.contaFinanceiraCodigo} onChange={e => selecionarContaFinanceira(index, e.value)}
                                            options={contasFinanceiras} optionValue="codigo" optionLabel={rotuloContaFinanceira}
                                            placeholder={carregandoContas ? "Carregando..." : "Código ou nome da conta"}
                                            filterPlaceholder="Buscar por código ou nome..." filter disabled={carregandoContas} />
                                    </td>
                                    <td className="px-3 py-2">
                                        <InputMoney
                                            minFractionDigits={2}
                                            maxFractionDigits={2}
                                            mode="decimal"
                                            onFocus={e => e.target.select()}
                                            value={item.valor}
                                            onChange={e => atualizar(index, { valor: e.target.value })}
                                        />
                                    </td>
                                    <td className="px-3 py-2">
                                        <Input type="text" value={item.numeroNotaFiscal ?? ""} onChange={e => atualizar(index, { numeroNotaFiscal: e.target.value })} />
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                        <button
                                            type="button"
                                            title="Remover"
                                            onClick={() => remover(index)}
                                            className="h-7 w-7 inline-flex items-center justify-center rounded-full text-destructive hover:bg-destructive/10"
                                        >
                                            <i className="fa fa-trash" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className={`px-3 py-2 text-sm font-medium border-t border-border ${totalConfere ? "text-success" : "text-destructive"}`}>
                        Total rateado: {formatarMoeda(totalRateado)}
                        {Number(valorTotal) > 0 && ` / ${formatarMoeda(valorTotal)}`}
                    </div>
                </div>
            )}

            <div>
                <Button variant="outline" label="Adicionar Rateio" iconLeft="fa fa-plus" onClick={adicionar} />
            </div>
        </div>
    );
}
